import type { HTMLAttributes } from 'react';
import { cn } from '@virginexperiencedays/tailwind';

import { VisualHeading } from '../../../typography/VisualHeading';
import { Image } from '../../../layout/Image';
import { Grid } from '../../../layout/Grid';
import { LinkWrap } from '../../../navigation/LinkWrap';

export type Author = {
  name: string;
  avatar?: AvatarType;
  bio: string;
  _meta?: {
    uid: string;
  };
};

type AvatarType = {
  url: string;
  alt: string;
  dimensions: { width: number; height: number };
};

type Props = {
  /**
   * is an object that takes the following props:
   * @param {AvatarType} avatar - Author's avatar url
   * @param {string} name - Author's name
   * @param {string} bio - Author's bio
   */
  author: Author;
} & HTMLAttributes<HTMLElement>;

export const BlogPostAuthor = ({ author, className }: Props) => {
  const { name, avatar, bio } = author;
  const postProps = {
    className,
    avatar,
    name,
    bio,
  };

  if (author?._meta?.uid) {
    return (
      <LinkWrap
        data-testid="blog-post-author"
        href={`/experience-blog/${encodeURIComponent(author?._meta?.uid)}`}
      >
        <Post {...postProps} />
      </LinkWrap>
    );
  }

  return <Post {...postProps} />;
};

const Post = ({ className, avatar, name, bio, ...props }: HTMLAttributes<HTMLElement> & Author) => (
  <Grid
    className={cn(
      'grid-cols-[minmax(0,_48px)_1fr] sm:grid-cols-[minmax(0,_48px)_1fr] md:grid-cols-[minmax(0,_100px)_1fr] lg:grid-cols-[minmax(0,_100px)_1fr] xl:grid-cols-[minmax(0,_100px)_1fr]',
      className
    )}
    space="none"
    dataTestId="blog-post-author"
    {...props}
  >
    {!!avatar?.url && (
      <Image
        className="col-start-1 col-end-2 row-start-1 row-end-2 self-start md:row-end-3 md:self-end [&_img]:rounded-full"
        src={avatar?.url}
        alt={name}
        width={avatar?.dimensions?.width ?? 100}
        height={avatar?.dimensions?.height ?? 100}
        sizes="5vw"
        dataTestId="author-image"
        imageObject={avatar}
      />
    )}
    <VisualHeading
      className="text-neutral col-start-2 col-end-3 row-start-1 row-end-2 mx-4 my-2 self-center text-[1.2rem] font-normal md:mx-6 md:my-0 md:self-end"
      size="6"
      as="h2"
      data-testid="author-name"
    >
      By {name}
    </VisualHeading>
    <p
      className="text-neutral col-start-1 col-end-3 row-start-2 row-end-3 mt-4 font-normal leading-normal md:col-start-2 md:mt-2 md:self-start md:px-6 md:py-0 md:text-sm"
      data-testid="author-bio"
    >
      {bio}
    </p>
  </Grid>
);
